import { useCallback, useMemo } from 'react'

import { getContract } from 'thirdweb'
import { approve } from 'thirdweb/extensions/erc20'
import { useSendTransaction } from 'thirdweb/react'

import Client from '../client'
import {
  CHAIN_IDS_TO_TOKEN_MESSENGER_ADDRESSES,
  CHAIN_IDS_TO_USDC_ADDRESSES,
} from '../constants/address'
import { CHAIN_IDS_TO_CHAINS } from '../constants/chain'

import type { SupportedChainId } from '../constants/chain'

/**
 * Returns an approve method that can be used to approve USDC allowance with its states.
 * @param chainId - The chain ID that given USDC contract address is on.
 */
export const useUsdcApproval = (chainId: SupportedChainId) => {
  const client = Client.instance
  const chain = CHAIN_IDS_TO_CHAINS[chainId]
  const contractAddress = CHAIN_IDS_TO_USDC_ADDRESSES[chainId]
  const spenderAddress = CHAIN_IDS_TO_TOKEN_MESSENGER_ADDRESSES[chainId]

  const contract = useMemo(
    () =>
      getContract({
        client,
        chain,
        address: contractAddress,
      }),
    [chain, client, contractAddress],
  )

  const {
    mutate: sendTransaction,
    data,
    error,
    isPending,
    isSuccess,
  } = useSendTransaction({ payModal: false })

  const approveTokenAllowance = useCallback(
    (amountToApprove: number | string) => {
      const transaction = approve({
        contract,
        spender: spenderAddress,
        amount: amountToApprove,
      })
      sendTransaction(transaction)
    },
    [contract, sendTransaction, spenderAddress],
  )

  return {
    approve: approveTokenAllowance,
    data,
    error,
    isPending,
    isSuccess,
  }
}
